import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

export interface LeagueScheduledMatch {
  id: string;
  event_id: string;
  encounter_number: number;
  match_number: number;
  scheduled_date: string;
  scheduled_time: string;
  court_number: number;
  team1_player1_name: string;
  team1_player2_name?: string;
  team2_player1_name: string;
  team2_player2_name?: string;
  team1_registration_id: string;
  team2_registration_id: string;
  status: string;
}

export type OpponentAttendanceStatus = 'confirmed' | 'not_attending' | 'pending';

interface UseLeagueMatchesResult {
  matches: LeagueScheduledMatch[];
  matchesByEncounter: Record<number, LeagueScheduledMatch[]>;
  userMatchForEncounter: LeagueScheduledMatch | null;
  userMatchesForEncounter: LeagueScheduledMatch[];
  userTeam: { player1: string; player2?: string } | null;
  opponentTeam: { player1: string; player2?: string } | null;
  opponentAttendanceStatus: OpponentAttendanceStatus;
  isLoading: boolean;
  error: string | null;
  selectedEncounter: number;
  setSelectedEncounter: (encounter: number) => void;
  // New: all matches for the league (including elimination phases)
  allScheduledMatches: LeagueScheduledMatch[];
  allMatchesByEncounter: Record<number, LeagueScheduledMatch[]>;
}

export function useLeagueMatches(eventId: string, initialEncounter?: number): UseLeagueMatchesResult {
  const [selectedEncounter, setSelectedEncounter] = useState<number>(initialEncounter || 1);

  // Fetch user's registration for this league
  const { data: userRegistrations } = useQuery({
    queryKey: ['user-league-registrations', eventId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('registrations')
        .select('id, user_id, partner_user_id, player1_name, player2_name')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`);

      if (error) throw error;

      // Resolve partner names from profiles if player2_name is empty but partner_user_id exists
      const enriched = await Promise.all((data || []).map(async (reg) => {
        if (!reg.player2_name && reg.partner_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', reg.partner_user_id)
            .single();
          return { ...reg, player2_name: profile?.full_name || null };
        }
        return reg;
      }));

      return enriched;
    },
    enabled: !!eventId
  });

  // For backward compatibility, keep first registration as userRegistration
  const userRegistration = (userRegistrations && userRegistrations.length > 0) ? userRegistrations[0] : null;
  const userRegIds = useMemo(() => (userRegistrations || []).map(r => r.id), [userRegistrations]);

  // Fetch all scheduled matches for this league
  const { data: matchesData, isLoading: matchesLoading, error: matchesError } = useQuery({
    queryKey: ['league-scheduled-matches', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('league_scheduled_matches')
        .select('*')
        .eq('event_id', eventId)
        .order('encounter_number', { ascending: true })
        .order('match_number', { ascending: true });

      if (error) throw error;
      return (data || []) as LeagueScheduledMatch[];
    },
    enabled: !!eventId
  });

  const allMatches = matchesData || [];

  // Organize ALL matches by encounter (for elimination phases display)
  const allMatchesByEncounter = useMemo(() => {
    const byEncounter: Record<number, LeagueScheduledMatch[]> = {};
    allMatches.forEach((match) => {
      if (!byEncounter[match.encounter_number]) {
        byEncounter[match.encounter_number] = [];
      }
      byEncounter[match.encounter_number].push(match);
    });
    // Sort each encounter's matches by match_number
    Object.values(byEncounter).forEach(matches => {
      matches.sort((a, b) => a.match_number - b.match_number);
    });
    return byEncounter;
  }, [allMatches]);

  // Get user's player name for name-based matching (rotating partner formats)
  const userName = useMemo(() => {
    return userRegistrations?.[0]?.player1_name || null;
  }, [userRegistrations]);

  // Filter matches where user participates (by registration ID or player name)
  const userMatches = useMemo(() => {
    if ((!userRegIds.length && !userName) || !allMatches.length) return [];
    
    return allMatches.filter(m => {
      // Match by registration ID (existing logic)
      const matchByRegId = userRegIds.includes(m.team1_registration_id) || 
                           userRegIds.includes(m.team2_registration_id);
      if (matchByRegId) return true;
      
      // Match by player name (for rotating partner formats)
      if (userName) {
        return m.team1_player1_name === userName || 
               m.team1_player2_name === userName ||
               m.team2_player1_name === userName || 
               m.team2_player2_name === userName;
      }
      return false;
    });
  }, [allMatches, userRegIds, userName]);

  // Organize user's matches by encounter - store as ARRAY to support multiple matches per encounter
  const matchesByEncounter = useMemo(() => {
    const byEncounter: Record<number, LeagueScheduledMatch[]> = {};
    userMatches.forEach((match) => {
      if (!byEncounter[match.encounter_number]) {
        byEncounter[match.encounter_number] = [];
      }
      byEncounter[match.encounter_number].push(match);
    });
    // Sort each encounter's matches by match_number
    Object.values(byEncounter).forEach(matches => {
      matches.sort((a, b) => a.match_number - b.match_number);
    });
    return byEncounter;
  }, [userMatches]);

  // Get all matches for the selected encounter
  const userMatchesForEncounter = matchesByEncounter[selectedEncounter] || [];
  
  // Get the first match for backward compatibility
  const userMatchForEncounter = userMatchesForEncounter[0] || null;

  // Extract opponent registration ID first
  const opponentRegistrationId = useMemo(() => {
    if (!userMatchForEncounter || !userRegistration) return null;
    const isUserTeam1 = userMatchForEncounter.team1_registration_id === userRegistration.id;
    return isUserTeam1 ? userMatchForEncounter.team2_registration_id : userMatchForEncounter.team1_registration_id;
  }, [userMatchForEncounter, userRegistration]);

  // Fetch opponent's registration data (user IDs + partner name)
  const { data: opponentRegData } = useQuery({
    queryKey: ['opponent-registration', opponentRegistrationId],
    queryFn: async () => {
      if (!opponentRegistrationId) return null;

      const { data, error } = await supabase
        .from('registrations')
        .select('user_id, partner_user_id, player2_name')
        .eq('id', opponentRegistrationId)
        .single();

      if (error) throw error;

      // Resolve partner name from profiles if missing
      let resolvedPartnerName = data?.player2_name || null;
      if (!resolvedPartnerName && data?.partner_user_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', data.partner_user_id)
          .single();
        resolvedPartnerName = profile?.full_name || null;
      }

      const ids: string[] = [];
      if (data?.user_id) ids.push(data.user_id);
      if (data?.partner_user_id) ids.push(data.partner_user_id);
      return { userIds: ids, partnerName: resolvedPartnerName };
    },
    enabled: !!opponentRegistrationId
  });

  const opponentUserIds = opponentRegData?.userIds || [];
  const opponentResolvedPartnerName = opponentRegData?.partnerName || null;

  // Determine which team is the user and which is the opponent
  const { userTeam, opponentTeam } = useMemo(() => {
    if (!userMatchForEncounter || !userRegistration) {
      return { userTeam: null, opponentTeam: null };
    }

    const isUserTeam1 = userMatchForEncounter.team1_registration_id === userRegistration.id;
    const resolvedPartnerName = userRegistration.player2_name || null;
    
    if (isUserTeam1) {
      return {
        userTeam: {
          player1: userMatchForEncounter.team1_player1_name,
          player2: userMatchForEncounter.team1_player2_name || resolvedPartnerName
        },
        opponentTeam: {
          player1: userMatchForEncounter.team2_player1_name,
          player2: userMatchForEncounter.team2_player2_name || opponentResolvedPartnerName
        }
      };
    } else {
      return {
        userTeam: {
          player1: userMatchForEncounter.team2_player1_name,
          player2: userMatchForEncounter.team2_player2_name || resolvedPartnerName
        },
        opponentTeam: {
          player1: userMatchForEncounter.team1_player1_name,
          player2: userMatchForEncounter.team1_player2_name || opponentResolvedPartnerName
        }
      };
    }
  }, [userMatchForEncounter, userRegistration, opponentResolvedPartnerName]);

  // Fetch opponent attendance status for the selected encounter
  const { data: opponentAttendance } = useQuery({
    queryKey: ['opponent-attendance', eventId, selectedEncounter, JSON.stringify(opponentUserIds || [])],
    queryFn: async () => {
      if (!opponentUserIds || opponentUserIds.length === 0) return null;

      const { data, error } = await supabase
        .from('league_attendance')
        .select('user_id, status')
        .eq('event_id', eventId)
        .eq('week_number', selectedEncounter)
        .in('user_id', opponentUserIds);

      if (error) throw error;
      return data || [];
    },
    enabled: !!eventId && Array.isArray(opponentUserIds) && opponentUserIds.length > 0,
    staleTime: 0,
    refetchOnMount: true
  });

  // Determine opponent attendance status
  const opponentAttendanceStatus: OpponentAttendanceStatus = useMemo(() => {
    if (!opponentAttendance || opponentAttendance.length === 0) {
      return 'pending';
    }

    const hasConfirmed = opponentAttendance.some(a => a.status === 'confirmed');
    const hasNotAttending = opponentAttendance.some(a => a.status === 'not_attending');

    if (hasNotAttending) return 'not_attending';
    if (hasConfirmed) return 'confirmed';
    return 'pending';
  }, [opponentAttendance]);

  return {
    matches: userMatches,
    matchesByEncounter,
    userMatchForEncounter,
    userMatchesForEncounter,
    userTeam,
    opponentTeam,
    opponentAttendanceStatus,
    isLoading: matchesLoading,
    error: matchesError?.message || null,
    selectedEncounter,
    setSelectedEncounter,
    // Expose all matches for elimination phases
    allScheduledMatches: allMatches,
    allMatchesByEncounter
  };
}
